import { Dropdown } from '@iveco-ti/iveco-ds'
import { Grip, PanelRightClose } from 'lucide-react'
import { Navigate, Outlet } from 'react-router'

import { AbilityContext } from '@/lib/auth/abilityContext'
import { defineAbilityFor } from '@/lib/auth/casl'
import { AvatarMenu } from '../components/AvatarMenu'
import { FeatureFlag } from '../components/FeatureFlag'
import { LoadingFallback } from '../components/LoadingFallback'
import { NotificationMenu } from '../components/NotificationMenu'
import { QuickAccessTools } from '../components/QuickAccessTools'
import { Sidebar } from '../components/Sidebar'
import { ROUTES } from '../constants/routes'
import { useActiveGeneralSettings } from '../hooks/useActiveGeneralSettings'
import { useGetUserInfo } from '../hooks/useGetUserInfo'
import { QuickAccess } from '../models/generalSettings'

export function AuthenticatedLayout() {
  const { userInfo, isPendingUserInfo } = useGetUserInfo()
  const { settings } = useActiveGeneralSettings()

  if (isPendingUserInfo) {
    return <LoadingFallback />
  }

  if (!userInfo) {
    const isSigningOut = sessionStorage.getItem('isSigningOut')

    if (isSigningOut) {
      return null
    }

    return <Navigate to={ROUTES.LOGIN.SIGN_IN} replace />
  }

  const userAbility = defineAbilityFor(userInfo)

  return (
    <AbilityContext.Provider value={userAbility}>
      <header className="lg:ms-64 fixed top-0 inset-x-0 flex flex-wrap md:justify-start md:flex-nowrap z-40 bg-white border-b border-gray-200">
        <div className="flex justify-between basis-full items-center w-full py-2.5 px-2 sm:px-5">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="lg:hidden text-brand-900"
              data-hs-overlay="#sidebar"
            >
              <PanelRightClose />
            </button>
          </div>

          <div className="flex items-center gap-4">
            <FeatureFlag flag="display_notification_menu">
              <NotificationMenu />
            </FeatureFlag>

            {settings?.toolsMenu && (
              <Dropdown.Root>
                <Dropdown.Trigger asChild>
                  <Grip className="h-5 w-5 text-gray-500 hover:text-gray-700 cursor-pointer" />
                </Dropdown.Trigger>

                <Dropdown.Body className="p-2 border border-base-200 min-w-fit">
                  {settings?.features.show_quick_access ===
                    QuickAccess.TOOLS && <QuickAccessTools />}
                </Dropdown.Body>
              </Dropdown.Root>
            )}

            <AvatarMenu />
          </div>
        </div>
      </header>

      <Sidebar />

      <main className="lg:ps-64 pt-16">
        <div className="p-5 bg-base-50 h-[calc(100vh-68px)] flex flex-col gap-5">
          <Outlet />
        </div>
      </main>
    </AbilityContext.Provider>
  )
}
